import { Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, Loader } from "common";
import { sumBy } from "lodash";
import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Permission, Product, SourceCopy, Transaction } from "types";
import { Currency } from "@/components/Common/Currency";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { PaymentMethodIcon } from "@/components/Common/PaymentMethodIcon";
import { ProductImage } from "@/components/Common/ProductImage";
import { SourceIcon, SourceName } from "@/components/Common/SourceIcon";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useResource } from "@/hooks/useResource";
import { isAmountIncludesPartnerFee, showPartnerFee } from "@/models/PartnerFeeFormula";
import { getPaymentMethodName } from "@/models/Payment";
import { getGuestFee, getMerchandiseFee, getPartnerFee } from "@/models/Transaction";

export const PayoutDetailsTable = () => {
    const history = useHistory();
    const seller = useSeller();
    const { isGranted } = useAuthorization();
    const params = useParams<{ id: ID }>();
    const { data: transactions, isLoading } = useInfiniteCollection<Transaction>("transactions", { payout: params.id });

    const showGuestFee = useMemo(() => {
        return isAmountIncludesPartnerFee(seller.partnerFeeFormulas);
    }, [seller]);

    const showMerchandiseFee = isGranted(Permission.MERCHANDISE_VIEW);

    const handleItemClick = (purchaseId?: string) => {
        history.push(`/purchases/${purchaseId}`);
    };

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Item</Table.Header>
                        <Table.Header>Transaction Date</Table.Header>
                        <Table.Header>Payout Date</Table.Header>
                        <Table.Header>Gross</Table.Header>
                        <Table.Header>Processing Fee</Table.Header>
                        {showPartnerFee() && <Table.Header>Service Fee</Table.Header>}
                        {showGuestFee && <Table.Header>Guest Fee</Table.Header>}
                        {showMerchandiseFee && <Table.Header>Merchandise Fee</Table.Header>}
                        <Table.Header>Taxes/Fees</Table.Header>
                        <Table.Header>Net</Table.Header>
                        <Table.Header>Balance</Table.Header>
                        <Table.Header>Source</Table.Header>
                        <Table.Header>Method</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {transactions.map((transaction) => {
                        const productItem = transaction.items[0];
                        const product = productItem.orderItem?.product;
                        return (
                            <Table.Row
                                key={transaction.id}
                                className="cursor-pointer hover:bg-gray-lighter"
                                onClick={() => handleItemClick(transaction.purchase?.id)}
                            >
                                <Table.Cell>
                                    <div className="w-15">
                                        {productItem.orderItem && (
                                            <Tooltip content={<ProductNameTooltip productRef={product} />}>
                                                <ProductImage product={product} size="small" />
                                            </Tooltip>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell className="no-translate">
                                    {formatDate(transaction.createdAt, DateFormat.DATE_SHORT)}
                                </Table.Cell>
                                <Table.Cell className="no-translate">
                                    {transaction.payout?.createdAt
                                        ? formatDate(transaction.payout.createdAt, DateFormat.DATE_TIME_SHORT)
                                        : ""}
                                </Table.Cell>
                                <Table.Cell className={clsx(transaction.amount < 0 && "text-red")}>
                                    <Currency>{sumBy(transaction.items, "gross")}</Currency>
                                </Table.Cell>
                                <Table.Cell className={clsx(transaction.amount < 0 && "text-red")}>
                                    <Currency>{sumBy(transaction.items, "commission")}</Currency>
                                </Table.Cell>
                                {showPartnerFee() && (
                                    <Table.Cell>
                                        <PartnerFee transaction={transaction} />
                                    </Table.Cell>
                                )}
                                {showGuestFee && (
                                    <Table.Cell>
                                        <GuestFee transaction={transaction} />
                                    </Table.Cell>
                                )}
                                {showMerchandiseFee && (
                                    <Table.Cell>
                                        <MerchandiseFee transaction={transaction} />
                                    </Table.Cell>
                                )}
                                <Table.Cell className={clsx(transaction.balance < 0 && "text-red")}>
                                    <Currency>{sumBy(transaction.items, "taxFee")}</Currency>
                                </Table.Cell>
                                <Table.Cell className={clsx(transaction.balance < 0 && "text-red")}>
                                    <Currency>{sumBy(transaction.items, "net")}</Currency>
                                </Table.Cell>
                                <Table.Cell className={clsx(transaction.balance < 0 && "text-red")}>
                                    <Currency>{transaction.balance}</Currency>
                                </Table.Cell>
                                <Table.Cell>
                                    <SourceIcon source={transaction.source} size="small" className="top-0 mr-2" />
                                    {SourceCopy[transaction.source] ?? <SourceName source={transaction.source} />}
                                </Table.Cell>
                                <Table.Cell>
                                    {transaction.method && (
                                        <>
                                            <PaymentMethodIcon
                                                size="small"
                                                method={transaction.method}
                                                className="top-0 mr-2"
                                            />
                                            {getPaymentMethodName(seller, transaction.method)}
                                        </>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>

            {isLoading && <Loader />}

            {transactions.length === 0 && !isLoading ? (
                <div className="my-2 text-center text-base text-gray-dark">
                    No Payout data found. Try choosing a different date range
                </div>
            ) : null}
        </>
    );
};

const PartnerFee = ({ transaction }: { transaction: Transaction }) => {
    const fee = getPartnerFee(transaction);

    return (
        <div className={clsx(fee < 0 ? "text-red" : "text-gray-darker")}>
            <Currency>{fee}</Currency>
        </div>
    );
};

const GuestFee = ({ transaction }: { transaction: Transaction }) => {
    const fee = getGuestFee(transaction);

    return (
        <div className={clsx(fee < 0 ? "text-red" : "text-gray-darker")}>
            <Currency>{fee}</Currency>
        </div>
    );
};

const MerchandiseFee = ({ transaction }: { transaction: Transaction }) => {
    const fee = getMerchandiseFee(transaction);

    return (
        <div className={clsx(fee < 0 ? "text-red" : "text-gray-darker")}>
            <Currency>{fee}</Currency>
        </div>
    );
};

const ProductNameTooltip = ({ productRef }: { productRef: Product }) => {
    const { data: product } = useResource<Product>(["products", productRef?.id]);
    return <NoTranslate as="div">{product?.name}</NoTranslate>;
};
